import React, { useCallback, useEffect, useRef, useState } from "react";
import { useRouteMatch, Switch, useLocation } from "react-router-dom";
import { PrivateRoute } from "../../components/PrivateRoute";
import { useSelector } from "react-redux";
import PersistentBoaEmbed from "../../components/persistentBoaEmbed";
import { AnalyticsMode } from "../../models/organization/organizationInterfaces";
import { selectOrg } from "../../models/organization/organizationSlice";
import { selectMyUser } from "../../models/users/myUserSlice";
import Unauthorized from "../Unauthorized";
import api from "../../utils/api";
import { canAccess } from "../../utils/user";

interface AnalyticsEmbed {
  embedUrl: string;
  expiresAt: string;
}

/** Re-mint this long before the token expires so the iframe never blanks out. */
const REFRESH_LEAD_MS = 60_000;

const AnalyticsEmbedFrame = ({ orgSlug }: { orgSlug: string }) => {
  const [embedUrl, setEmbedUrl] = useState<string>();
  const [error, setError] = useState<string>();
  const refreshTimer = useRef<ReturnType<typeof setTimeout>>();

  const loadEmbed = useCallback(async () => {
    try {
      const { data } = await api.get<AnalyticsEmbed>(
        `/organizations/${orgSlug}/analytics/embed`
      );
      setError(undefined);
      setEmbedUrl(data.embedUrl);

      // Sigma embed tokens are short-lived. Schedule the next mint just before
      // this one lapses so an analyst reading a dashboard is not interrupted.
      const msUntilRefresh =
        new Date(data.expiresAt).getTime() - Date.now() - REFRESH_LEAD_MS;
      if (msUntilRefresh > 0) {
        refreshTimer.current = setTimeout(loadEmbed, msUntilRefresh);
      }
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response
        ?.status;
      // Fail closed. There is deliberately no fallback dashboard: showing another
      // institution's analytics would be worse than showing none.
      setEmbedUrl(undefined);
      if (status === 404) {
        setError("Analytics has not been configured for this organization.");
      } else if (status === 401 || status === 403) {
        // Permanent for this user — retrying will not help, so do not imply it.
        setError("You do not have access to analytics for this organization.");
      } else {
        setError(
          "Analytics is temporarily unavailable. Please try again shortly."
        );
      }
    }
  }, [orgSlug]);

  useEffect(() => {
    loadEmbed();
    return () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    };
  }, [loadEmbed]);

  // These states sit inside a `bg-secondary` wrapper sized for the iframe.
  // `text-muted` is the same grey as `bg-secondary` in Bootstrap 4, so give
  // them their own light surface — otherwise the message is invisible and a
  // fail-closed page reads as a blank screen.
  if (error) {
    return (
      <div className="d-flex align-items-center justify-content-center h-100 bg-white">
        <p className="text-muted mb-0">{error}</p>
      </div>
    );
  }

  if (!embedUrl) {
    return (
      <div className="d-flex align-items-center justify-content-center h-100 bg-white">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading analytics…</span>
        </div>
      </div>
    );
  }

  return (
    <iframe
      title="Analytics"
      src={embedUrl}
      frameBorder="0"
      width="100%"
      height="100%"
    />
  );
};

/** Sigma, the native dashboard, or the native dashboard on sample data. */
export type AnalyticsView = AnalyticsMode | "mock";

/**
 * Which frame to show. `?mode=native` forces the native dashboard over a Sigma
 * workbook and `?mode=mock` shows it on fixed sample data; neither grants
 * anything, since the `/analytics/*` API checks roles itself and mock makes no
 * API calls. Otherwise the organization's mode decides. An API too old to send
 * one gets the same rule from `sigmaUrl`, where only the `"native"` sentinel
 * leaves Sigma.
 */
export const resolveAnalyticsView = (
  orgMode: AnalyticsMode | undefined,
  sigmaUrl: string | undefined,
  modeParam: string | null
): AnalyticsView => {
  if (modeParam === "native" || modeParam === "mock") {
    return modeParam;
  }
  return orgMode ?? (sigmaUrl === "native" ? "native" : "sigma");
};

/** Which frame this user gets for this organization, and whether any. */
const useAnalyticsAccess = () => {
  const location = useLocation();
  const organization = useSelector(selectOrg);
  const user = useSelector(selectMyUser);
  const view = resolveAnalyticsView(
    organization?.meta?.analyticsMode,
    organization?.meta?.sigmaUrl,
    new URLSearchParams(location.search).get("mode")
  );
  // Mirror the API's AnalyticsPolicy, which allows portalAdmin and analyst but
  // not portalUser. `isAdminPortalUser` is broader — it includes portalUser —
  // so gating on it would let a portalUser render a frame the mint endpoint
  // then refuses with a 403. The API enforces this regardless; matching it here
  // avoids showing a transient-error message for a permanent denial. Sample
  // data reads nothing, so it is open to anyone on the page.
  const allowed =
    view === "mock" ||
    Boolean(user?.roles && canAccess(user.roles, ["portalAdmin", "analyst"]));
  return { view, allowed, user, orgSlug: organization?.slug };
};

/**
 * The native dashboard, served by boa. Rendered by the organization page
 * BESIDE its route Switch, as the Documents tab renders its disclosures: a
 * route unmounts when the user opens another tab, and with it the iframe and
 * the booted boa app, so every return to Analytics would reboot it and replay
 * every section request. Here it mounts on the first visit and then persists,
 * hidden, while the user works elsewhere.
 *
 * The portal's query string is handed through once, at that first visit, so a
 * deep link (`?preset=90d&flowIds=…`, `?mode=mock`) lands in the frame; after
 * that the dashboard owns its URL. `path` is latched with the organization and
 * view it was built for, which are also the instance key: a path that changed
 * would navigate the live frame and lose the persistence this exists for.
 */
export const PersistentAnalyticsFrame = () => {
  const { path: orgPath } = useRouteMatch();
  const onAnalytics = Boolean(
    useRouteMatch({ path: `${orgPath}/analytics`, exact: true })
  );
  const { search } = useLocation();
  const { view, allowed, orgSlug } = useAnalyticsAccess();
  const [opened, setOpened] = useState<{
    orgSlug: string;
    key: string;
    path: string;
  }>();
  const show = onAnalytics && allowed && view !== "sigma" && !!orgSlug;
  const key = `${orgSlug}|${view}`;

  useEffect(() => {
    if (show && orgSlug && opened?.key !== key) {
      setOpened({
        orgSlug,
        key,
        path: `/organization/${orgSlug}/analytics${search}`,
      });
    }
  }, [show, orgSlug, key, opened, search]);

  // Other tabs drop `?mode`, so the latched frame outlives a view change made
  // elsewhere and is replaced only when Analytics opens on a different one.
  const ready = !!orgSlug && opened?.orgSlug === orgSlug;
  return (
    // Same canvas as the operations dashboard, so nothing dark shows while
    // the frame loads; empty, it takes no space.
    <div style={{ background: "#f6f6f6" }}>
      {ready && (
        // The latch above holds the path, which the embed's own `active` latch
        // cannot; with `active={ready}` that inner latch never gates mounting.
        <PersistentBoaEmbed
          active={ready}
          visible={show}
          instanceKey={opened!.key}
          path={opened!.path}
          title="Analytics"
          initialHeight={1400}
        />
      )}
    </div>
  );
};

export const AnalyticsRouter = () => {
  let { path } = useRouteMatch();
  const { view, allowed, user, orgSlug } = useAnalyticsAccess();

  if (!allowed) {
    return <Unauthorized userId={user?.userId} />;
  }

  return (
    <Switch>
      <PrivateRoute exact path={`${path}`}>
        {/* The native frame is PersistentAnalyticsFrame, beside the
            organization's Switch, so that leaving this route keeps it alive. */}
        {view === "sigma" && (
          <div className="bg-secondary" style={{ height: "93vh" }}>
            {orgSlug && <AnalyticsEmbedFrame orgSlug={orgSlug} />}
          </div>
        )}
      </PrivateRoute>
    </Switch>
  );
};
